import { GaAction } from '@ga/ui-components/action'

import { useHoverFocus, useSlot, useStockStatus } from '../../../composables'
import { DISCLAIMER_TOOLTIP_PLACEMENT, ROOT_TEST_ID } from '../../../constants'
import { GaProductCardCartAdapter } from '../../adapters/cart-adapter'
import { GaProductCardFavoriteAdapter } from '../../adapters/favorite-adapter'
import { GaProductCardAttributes } from '../../molecules/attributes'
import { GaProductCardMedia, VIDEO_STRATEGY } from '../../molecules/media'
import { GaProductCardName } from '../../molecules/name'
// import { GaProductCardPrice } from '../../molecules/price'
import { GaProductCardStatus } from '../../molecules/status'

import { useMods, useSizes } from './scripts/composables'
import { INFO_INDENT, RATIO, SIZE } from './scripts/consts'

// @vue/component
export default {
  name: 'ga-product-card-vertical',

  components: {
    GaAction,

    // GaProductCardPrice,
    GaProductCardStatus,
    GaProductCardFavoriteAdapter,
    GaProductCardCartAdapter,
    GaProductCardMedia,
    GaProductCardAttributes,
    GaProductCardName,
  },

  props: {
    product: {
      type: Object,
      default: null,
    },

    size: {
      type: String,
      default: SIZE.M,
      validator: (value) => Object.values(SIZE).includes(value),
    },

    ratio: {
      type: String,
      default: RATIO.SQUARE,
      validator: (value) => Object.values(RATIO).includes(value),
    },

    reversed: {
      type: Boolean,
      default: false,
    },

    forceShowAttrs: {
      type: Boolean,
      default: false,
    },

    cancelDefaultAddToCart: {
      type: Boolean,
      default: false,
    },

    cancelDefaultUpdateQty: {
      type: Boolean,
      default: false,
    },

    cartLoading: {
      type: Boolean,
      default: false,
    },

    showAddToCart: {
      type: Boolean,
      default: true,
    },

    showAddToFavorites: {
      type: Boolean,
      default: true,
    },

    showPriceCurrency: {
      type: Boolean,
      default: true,
    },

    skeletonEnable: {
      type: Boolean,
      default: true,
    },

    infoIndent: {
      type: String,
      default: INFO_INDENT.NONE,
      validator: (value) => Object.values(INFO_INDENT).includes(value),
    },

    tooltipPlacement: {
      type: String,
      default: DISCLAIMER_TOOLTIP_PLACEMENT.AUTO,
      validator: (value) =>
        Object.values(DISCLAIMER_TOOLTIP_PLACEMENT).includes(value),
    },

    maxCountMedia: {
      type: Number,
      default: 10,
    },

    rootTestId: {
      type: String,
      default: ROOT_TEST_ID,
    },

    videoStrategy: {
      type: String,
      default: VIDEO_STRATEGY.NONE,
    },

    // Позволяет включать css-свойство "content-visibility" c значением 'auto', для некоторых элементов
    contentVisibilityAuto: {
      type: Boolean,
      default: true,
    },
  },

  emits: [
    'click-card',
    'mouseenter-card',
    'click-add-to-favorite',
    'add-to-favorite',
    'click-add-to-cart',
    'add-to-cart',
    'open-preview',
  ],

  setup(props, { emit }) {
    const { $gaApp } = useNuxtApp()

    // refs
    const linkRef = ref()
    const rootRef = ref()
    const cartRef = ref()

    // props
    const { product, size, reversed, infoIndent, contentVisibilityAuto } =
      toRefs(props)

    // use
    const { isNotInStock, isWholeStock, isOutOfStock, productStatus } =
      useStockStatus(product)

    // view
    const { withHover, isHovering, highlight } = useHoverFocus(
      linkRef,
      rootRef,
      cartRef,
    )

    const { sizes } = useSizes(size)

    const slot = useSlot({
      product,
      sizes,
      props: { reversed },
      mods: { highlight },
    })

    const { mods } = useMods(
      size,
      reversed,
      highlight,
      isOutOfStock,
      isWholeStock,
      isNotInStock,
      infoIndent,
      withHover,
      contentVisibilityAuto,
    )

    const mediaListeners = {
      'video-end': () => emit('media-video-end'),
      'video-error': () => emit('media-video-error'),
      'video-start': () => emit('media-video-start'),
      'video-hover': ($event) => emit('media-video-hover', $event),
      'video-swipe': ($event) => emit('media-video-swipe', $event),
      'video-intersect': ($event) => emit('media-video-intersect', $event),
      'video-wrapper-hover': ($event) =>
        emit('media-video-wrapper-hover', $event),
    }

    const onProductCardClick = () => {
      $gaApp.services.app.bridge.sendOpenUrlMessage(
        `${$gaApp.services.app.main.baseUrl}${product.value.url}`,
      )

      emit('click-card')
    }

    return {
      linkRef,
      rootRef,
      cartRef,

      highlight,
      isHovering,
      mods,

      isNotInStock,
      productStatus,

      slot,
      sizes,
      mediaListeners,

      onProductCardClick,
    }
  },
}
